// Household: members + declarations (the protection-score inputs).

import { create } from 'zustand';

import { getDb, KV_HOUSEHOLD } from '@/db';
import type { Household, HouseholdDeclarations, HouseholdMember } from '@/types/domain';

/** Pure helper (safe under React Compiler memoization — see goals-store note) */
export function memberNameOf(household: Household | null, memberId: string): string {
  const member: HouseholdMember | undefined = household?.members.find((m) => m.id === memberId);
  if (!member) return '?';
  return member.id === 'mem-0001' ? 'Ty' : member.firstName; // P0: local user is always the owner
}

interface HouseholdState {
  hydrated: boolean;
  household: Household | null;
  hydrate: () => Promise<void>;
  setDeclarations: (declarations: HouseholdDeclarations) => Promise<void>;
  renameHousehold: (name: string) => Promise<void>;
  addMember: (input: { firstName: string; role: 'PARTNER' | 'CHILD'; phone?: string | null }) => Promise<void>;
  removeMember: (id: string) => Promise<void>;
}

export const useHouseholdStore = create<HouseholdState>((set, get) => ({
  hydrated: false,
  household: null,

  hydrate: async () => {
    const db = await getDb();
    const raw = await db.getKv(KV_HOUSEHOLD);
    set({ hydrated: true, household: raw ? (JSON.parse(raw) as Household) : null });
  },

  setDeclarations: async (declarations) => {
    const current = get().household;
    if (!current) return;
    const next: Household = { ...current, declarations, updatedAt: new Date().toISOString() };
    const db = await getDb();
    await db.setKv(KV_HOUSEHOLD, JSON.stringify(next));
    set({ household: next });
  },

  renameHousehold: async (name) => {
    const current = get().household;
    if (!current || !name.trim()) return;
    const next: Household = { ...current, name: name.trim(), updatedAt: new Date().toISOString() };
    const db = await getDb();
    await db.setKv(KV_HOUSEHOLD, JSON.stringify(next));
    set({ household: next });
  },

  addMember: async ({ firstName, role, phone = null }) => {
    const current = get().household;
    if (!current || !firstName.trim()) return;
    const member: HouseholdMember = {
      id: `mem-${Date.now().toString(36)}`,
      role,
      firstName: firstName.trim(),
      accountId: null, // real account linking = future backend step (honest copy in UI)
      birthYear: null,
      phone: phone?.trim() || null,
    };
    const next: Household = {
      ...current,
      members: [...current.members, member],
      updatedAt: new Date().toISOString(),
    };
    const db = await getDb();
    await db.setKv(KV_HOUSEHOLD, JSON.stringify(next));
    set({ household: next });
  },

  removeMember: async (id) => {
    const current = get().household;
    if (!current || id === 'mem-0001') return; // never remove the owner
    const next: Household = {
      ...current,
      members: current.members.filter((m) => m.id !== id),
      updatedAt: new Date().toISOString(),
    };
    const db = await getDb();
    await db.setKv(KV_HOUSEHOLD, JSON.stringify(next));
    set({ household: next });
  },
}));
